import type { AxiosInstance } from 'axios'
import type { Pinia } from 'pinia'
import { getAccessToken } from '@/helpers/auth.ts'
import {
    BAD_REQUEST_CODE,
    UNAUTHORIZED_CODE,
    UNPROCESSABLE_CONTENT_CODE,
} from '@/config/httpCodes.ts'
import { useAccountStore } from '@/stores/account.ts'
import router from '@/router'
import { routeNames } from '@/router/names.ts'
import { isAxiosError } from 'axios'
import { CHILD_PROTECT_DOCUMENT, RODO_DOCUMENT } from '@/config/requiredDocumentsCodes.ts'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'
import { useErrorStore } from '@/stores/error.ts'
import { showToast } from '@/services/modules/toastService.ts'
import i18n from '@/i18n'

export default (axios: AxiosInstance, pinia: Pinia) => {
    axios.interceptors.request.use((config) => {
        const token = getAccessToken()

        if (token && !config.omitAuthorization) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        return config
    })

    axios.interceptors.response.use(
        (response) => {
            return response
        },
        async (error) => {
            if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_CONTENT_CODE) {
                const errorStore = useErrorStore(pinia)

                errorStore.setGlobalError(error.response?.data?.message || '')

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.response?.data.error === CHILD_PROTECT_DOCUMENT) {
                await router.push({
                    name: routeNames.CHILD_PROTECTION_VIEW_NAME,
                })

                return Promise.reject(
                    new DocumentToAcceptError(
                        CHILD_PROTECT_DOCUMENT,
                        error.response.data.message || '',
                    ),
                )
            }

            if (isAxiosError(error) && error.response?.data.error === RODO_DOCUMENT) {
                await router.push({
                    name: routeNames.RODO_GUARDIAN_VIEW_NAME,
                })

                return Promise.reject(
                    new DocumentToAcceptError(RODO_DOCUMENT, error.response.data.message || ''),
                )
            }

            if (
                isAxiosError(error) &&
                error.status === UNAUTHORIZED_CODE &&
                error.config?.url === 'guardian/login_check'
            ) {
                // endpoint 'guardian/login_check' returns 401 on bad credentials and we don't want handle it like unauthorized response
                error.status = BAD_REQUEST_CODE

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.response?.status === UNAUTHORIZED_CODE) {
                const accountStore = useAccountStore(pinia)

                await accountStore.onUnauthorizedResponse()

                return Promise.reject(error)
            }

            if (isAxiosError(error) && error.code === 'ECONNABORTED') {
                showToast({
                    summary: i18n.global.t('Timeout error'),
                    severity: 'error',
                    life: 4000,
                })
            }

            return Promise.reject(error)
        },
    )
}
